import { Button, Card, CardContent, Input, Label } from "@exhousing/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { apiClient } from "@/api/client";
import { A4Sheet } from "@/components/document/A4Sheet";
import { useMe } from "@/hooks/api/useAuth";
import {
  type Letterhead,
  useLetterhead,
  useUpdateLetterhead,
} from "@/hooks/api/useLetterhead";

const FIELDS: { key: keyof Letterhead; label: string; placeholder: string }[] = [
  { key: "postal_address", label: "Postal address", placeholder: "Postbus 123, 1000 AA Amsterdam" },
  { key: "visit_address", label: "Visiting address", placeholder: "Hoofdstraat 1, 1000 AB Amsterdam" },
  { key: "phone", label: "Phone", placeholder: "+31 (0)20 123 4567" },
  { key: "email", label: "Email", placeholder: "info@youragency.nl" },
  { key: "website", label: "Website", placeholder: "www.youragency.nl" },
  { key: "iban", label: "IBAN", placeholder: "NL00BANK0123456789" },
  { key: "coc_number", label: "KVK / CoC number", placeholder: "12345678" },
  { key: "vat_number", label: "VAT / BTW number", placeholder: "NL123456789B01" },
];

const EMPTY: Letterhead = {
  postal_address: "", visit_address: "", phone: "", email: "",
  website: "", iban: "", coc_number: "", vat_number: "",
};

/** Joins non-empty parts with a separator — keeps the masthead tidy when fields are blank. */
function joinParts(parts: (string | undefined)[], sep: string): string {
  return parts.filter((p) => p && p.trim()).join(sep);
}

/** The live masthead + a greeked letter, mirroring the printed document base:
 *  crisp sans letterhead above a serif body. What prints at the top of every document. */
function LetterheadPreview({ form, orgName }: { form: Letterhead; orgName: string }) {
  const contact = joinParts(
    [form.phone && `T ${form.phone}`, form.email, form.website],
    "  ·  ",
  );
  const registration = joinParts(
    [form.iban && `IBAN ${form.iban}`, form.coc_number && `KVK ${form.coc_number}`, form.vat_number && `BTW ${form.vat_number}`],
    "  ·  ",
  );

  return (
    <div className="font-sans text-[13px] leading-relaxed text-neutral-800">
      <p className="text-base font-semibold tracking-tight">{orgName || "Your agency"}</p>
      <div className="mt-0.5 space-y-0.5 text-[11px] text-neutral-500">
        {form.postal_address && <p>Postadres: {form.postal_address}</p>}
        {form.visit_address && <p>Bezoekadres: {form.visit_address}</p>}
        {contact && <p>{contact}</p>}
        {registration && <p>{registration}</p>}
      </div>
      <hr className="my-5 border-neutral-200" />

      {/* Greeked letter body in serif — shows the letterhead in context. */}
      <div className="font-serif text-neutral-700">
        <p className="text-neutral-500">Amsterdam, 9 June 2026</p>
        <p className="mt-5">E. Tenant<br />Hoofdstraat 1<br />1000 AB Amsterdam</p>
        <p className="mt-5 font-medium">Concerning: your tenancy</p>
        <p className="mt-3">Dear Ms Tenant,</p>
        <p className="mt-3 text-justify text-neutral-400">
          This is a preview of how your letterhead sits above the body of every document
          your agency generates — contracts, invoices and letters. The sender details
          above update as you type; the text here is placeholder.
        </p>
        <p className="mt-3 text-justify text-neutral-300">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
          incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis
          nostrud exercitation ullamco laboris.
        </p>
        <p className="mt-5">Kind regards,</p>
        <p className="mt-1">{orgName || "Your agency"}</p>
      </div>
    </div>
  );
}

export function LetterheadPage() {
  const { data, isLoading } = useLetterhead();
  const { data: me } = useMe();
  const orgId = me?.organization_id;
  const { data: org } = useQuery({
    queryKey: ["organization", orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const { data: o, error } = await apiClient.GET(
        "/api/v1/organizations/{organization_id}",
        { params: { path: { organization_id: orgId! } } },
      );
      if (error || !o) throw new Error("Failed to load organization");
      return o;
    },
  });
  const update = useUpdateLetterhead();
  const [form, setForm] = useState<Letterhead>(EMPTY);

  useEffect(() => {
    if (data) setForm({ ...EMPTY, ...data });
  }, [data]);

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(form, {
      onSuccess: () => toast.success("Letterhead saved"),
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">Letterhead</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your agency&apos;s sender details, printed at the top of every generated document.
          The preview updates as you type.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <Card className="self-start">
          <CardContent className="pt-6">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <form onSubmit={onSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {FIELDS.map(({ key, label, placeholder }) => (
                  <div key={key} className="space-y-1.5">
                    <Label htmlFor={key}>{label}</Label>
                    <Input
                      id={key}
                      placeholder={placeholder}
                      value={form[key] ?? ""}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={update.isPending}>
                    {update.isPending ? "Saving…" : "Save letterhead"}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </p>
          <A4Sheet>
            <LetterheadPreview form={form} orgName={org?.name ?? ""} />
          </A4Sheet>
        </div>
      </div>
    </div>
  );
}
